import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Checkbox from '../../components/options/Checkbox';
import IconStar from '../../components/options/IconStar';

export type ProficiencyData = { weapons: string; armor: string; tools: string; languages: string };
export const emptyProficiencies: ProficiencyData = { weapons: '', armor: '', tools: '', languages: '' };

const armorOptions = ['Light', 'Medium', 'Heavy', 'Shields'] as const;
const armorKey = (entry: string) => entry.trim().toLowerCase().replace(/\s+armor$/, '').replace(/^shield$/, 'shields');

function ArmorProficiencies({ characterId, value, onChange }: {
    characterId: number; value: string; onChange: (field: keyof ProficiencyData, value: string) => void;
}) {
    const { apiRequest } = useAuth();
    const [draft, setDraft] = useState(value);
    const [pending, setPending] = useState(0);
    const queue = useRef<Promise<void>>(Promise.resolve());
    const [error, setError] = useState('');
    const entries = draft.split(/[,;\n]+/).map(entry => entry.trim()).filter(Boolean);
    const save = (next: string) => {
        setDraft(next);
        setPending(count => count + 1);
        setError('');
        queue.current = queue.current.then(async () => {
            try {
                await apiRequest(`/api/characters/${characterId}/proficiencies`, 'PATCH', { armor: next });
                onChange('armor', next);
                setError('');
            } catch {
                setError('Could not save. Try again.');
            } finally {
                setPending(count => count - 1);
            }
        });
    };
    return <div className="proficiency-field" role="group" aria-labelledby="armor-proficiencies-title">
        <div className="proficiency-field-title" id="armor-proficiencies-title"><i className="fa-solid fa-shield-halved" aria-hidden="true" /> Armor</div>
        <div className="armor-proficiency-options">
            {armorOptions.map(option => <Checkbox key={option} label={option} Icon={IconStar} size="1.25rem"
                checked={entries.some(entry => armorKey(entry) === armorKey(option))}
                onChange={checked => {
                    const next = entries.filter(entry => armorKey(entry) !== armorKey(option));
                    if (checked) next.push(option === 'Shields' ? 'Shields' : `${option} armor`);
                    save(next.join(', '));
                }} />)}
        </div>
        <span className="visually-hidden" role="status">{pending > 0 ? 'Saving armor proficiencies…' : ''}</span>
        {error && <small role="alert">{error} <button type="button" disabled={pending > 0} onClick={() => save(draft)}>Retry</button></small>}
    </div>;
}

function fitTextArea(input: HTMLTextAreaElement) {
    input.style.height = 'auto';
    const borderHeight = input.offsetHeight - input.clientHeight;
    input.style.height = `${input.scrollHeight + borderHeight}px`;
}

function ProficiencyField({ characterId, field, label, icon, value, onChange }: {
    characterId: number; field: keyof ProficiencyData; label: string; icon: string;
    value: string; onChange: (field: keyof ProficiencyData, value: string) => void;
}) {
    const { apiRequest } = useAuth();
    const [draft, setDraft] = useState(value);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef<HTMLTextAreaElement>(null);
    useLayoutEffect(() => {
        if (inputRef.current) fitTextArea(inputRef.current);
    }, [draft]);
    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;
        const resize = () => fitTextArea(input);
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', resize);
            return () => window.removeEventListener('resize', resize);
        }
        let width = input.getBoundingClientRect().width;
        const observer = new ResizeObserver(([entry]) => {
            if (entry.contentRect.width !== width) {
                width = entry.contentRect.width;
                resize();
            }
        });
        observer.observe(input);
        return () => observer.disconnect();
    }, []);
    const save = async () => {
        const next = draft.trim();
        if (next === value) { setError(''); return; }
        setSaving(true);
        setError('');
        try {
            await apiRequest(`/api/characters/${characterId}/proficiencies`, 'PATCH', { [field]: next });
            onChange(field, next);
            setDraft(next);
        } catch {
            setError('Could not save. Try again.');
        } finally {
            setSaving(false);
        }
    };
    return <div className="proficiency-field">
        <label htmlFor={`proficiency-${field}`}><i className={`fa-solid ${icon}`} aria-hidden="true" /> {label}</label>
        <textarea ref={inputRef} id={`proficiency-${field}`} rows={1} maxLength={2000} value={draft}
            placeholder="None added" disabled={saving} aria-invalid={!!error}
            aria-describedby={error ? `proficiency-${field}-error` : undefined}
            onChange={event => setDraft(event.target.value)} onBlur={save} />
        {saving && <small role="status">Saving…</small>}
        {error && <small id={`proficiency-${field}-error`} role="alert">{error} <button type="button" onClick={save}>Retry</button></small>}
    </div>;
}

export default function Proficiencies({ characterId, data, onChange }: {
    characterId: number; data: ProficiencyData;
    onChange: (field: keyof ProficiencyData, value: string) => void;
}) {
    return <section className="parchment-panel proficiencies-panel">
        <header className="panel-heading"><h2><i className="fa-solid fa-book" aria-hidden="true" /> Proficiencies</h2></header>
        <ArmorProficiencies characterId={characterId} value={data.armor} onChange={onChange} />
        <ProficiencyField characterId={characterId} field="weapons" label="Weapons" icon="fa-khanda" value={data.weapons} onChange={onChange} />
        <ProficiencyField characterId={characterId} field="tools" label="Tools" icon="fa-hammer" value={data.tools} onChange={onChange} />
        <ProficiencyField characterId={characterId} field="languages" label="Languages" icon="fa-language" value={data.languages} onChange={onChange} />
    </section>;
}
